import { useToken } from "@gluestack-style/react";
import CartApprovalHistory from "components/cart/CartApprovalHistory";
import { CartPickupLocationModal } from "components/cart/CartPickupLocationModal";
import { useUser } from "components/clerk";
import CheckoutProgress from "components/general/CheckoutProgress";
import DismissableContent from "components/general/DismissableContent";
import PageLoader from "components/general/PageLoader";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import { SiteSelectButton } from "components/general/SiteSelectButton";
import ItemCardList from "components/item/ItemCardList";
import ItemListItem from "components/item/ItemListItem";
import { Box, Button, HStack, Icon } from "components/ui";
import { H2, H3, H4, ParagraphSmall, Typography } from "components/ui/Typography";
import { router, useLocalSearchParams } from "expo-router";
import { Trash2, TriangleAlertIcon } from "lucide-react-native";
import React, { useEffect, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";
import { CartApproval, useGetCartByIdQuery, useGetCartCurrentQuery, useGetRecommendedQuery, usePostCartByIdClearMutation, usePutCartByIdMutation, usePutCartCurrentItemMutation } from "store/abcoApi";
import { Value } from "types/select.types";
import { useDataLayer } from "utils/hooks/useDataLayer";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useSiteRegion } from "utils/hooks/useSiteSection";
import { FlatList, Link, View } from "../components/ui";
import { CartCheckoutLayout, canBeFulfilled, sortLines } from "./CartCheckoutLayout";

export function CartScreen() {
	const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState<any>(null);
	const { id } = useLocalSearchParams<{ id: string }>();
	const { data: cart, isSuccess } = id ? useGetCartByIdQuery({ id: parseInt(id) }) : useGetCartCurrentQuery();
	const [isApprover, setIsApprover] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const [updateItem] = usePutCartCurrentItemMutation();
	const [updateCart] = usePutCartByIdMutation();
	const [postClearCartLines] = usePostCartByIdClearMutation();
	const isMobile = useIsMobile();
	const { isSignedIn } = useUser();
	const { height } = useWindowDimensions();
	const region = useSiteRegion();
	const { data: recommendedProductCollection } = useGetRecommendedQuery();
	const resolvedWarningMain = useToken("colors", "warningbright");
	const value = {
		id: cart?.siteId,
		name: cart?.siteName,
	} as unknown as Value;
	let { user } = useUser();

	const canEdit = useMemo(() => {
		if (!cart) return false;

		const currentUserId = parseInt(user?.externalId || "0", 10);
		const approvalHistory = cart.approvalHistory || [];

		const hasUserApproved = approvalHistory.some((entry) => entry.user?.id === currentUserId);
		if (hasUserApproved) {
			return false;
		}

		if (!cart.isCurrentCart) {
			return false;
		}

		const potentialApprovers = cart.potentialApprover || [];
		if (cart.awaitingApproval && potentialApprovers.length > 0) {
			const isApprover = potentialApprovers.some((approver) => approver.id === currentUserId);
			const isNotOwner = cart.userId !== currentUserId;
			return isApprover && isNotOwner;
		}
		return true;
	}, [cart, user?.externalId]);

	const updateSite = (site: Value) => {
		updateCart({
			cart: {
				siteId: site.id,
			},
			id: cart?.id as number,
		});
	};

	const handleRechargeableChange = async (stockcode: string, newValue: boolean, quantity?: number) => {
		try {
			await updateItem({
				stockcode: stockcode,
				quantity: quantity || 0,
				isRechargeable: newValue,
			});
		} catch (error) {
			console.log(error);
		}
	};

	const [cartLines, setCartLines]: any = useState([]);

	const { viewCart } = useDataLayer();
	const [hasPushedViewCart, setHasPushedViewCart] = useState(false);

	useEffect(() => {
		if (!cart || !isSuccess || hasPushedViewCart) return;

		viewCart();

		setHasPushedViewCart(true);
	}, [cart, isSuccess, hasPushedViewCart, viewCart]);

	useEffect(() => {
		if (cart?.hasCompleted) {
			setIsDisabled(true);
		}

		if (cart?.salesOrderId) {
			router.push(`/orders/S${cart.salesOrderId}?message=OrderComplete&cartId=CRT${cart.id}`);
			return;
		}

		let currentUserId = user && user.externalId ? parseInt(user.externalId ?? "") : "";
		if (currentUserId) {
			setIsApprover(cart?.potentialApprover?.some((approver: any) => approver.id === currentUserId) && cart.userId != currentUserId ? true : false);
		}

		if (isSuccess && cart) {
			const sortedLines = sortLines(cart?.lines || [], cart);
			setCartLines(sortedLines);
		}
	}, [cart, isSuccess]);

	const removeItem = (stockcode: string) => {
		let newCartLines = cartLines.filter((item: any) => item.item.stockcode !== stockcode);
		setCartLines(sortLines(newCartLines, cart));
		updateItem({
			stockcode: stockcode,
			quantity: 0,
		});
		// setCartData(newCart);
	};

	const clearCartLines = () => {
		postClearCartLines({
			id: cart?.id as number,
		});
	};

	if (!cart) {
		return (
			<ScrollContainer>
				<Box minHeight={150}>
					<PageLoader />
				</Box>
			</ScrollContainer>
		);
	}

	if (isMobile) {
		return (
			<CartCheckoutLayout canEdit={canEdit}>
				<Box style={{ flex: 1 }}>
					<FlatList
						data={cartLines}
						scrollEnabled={true}
						initialNumToRender={50}
						maxToRenderPerBatch={50}
						windowSize={50}
						// contentContainerStyle={{ paddingBottom: 120 }} // Add padding to prevent overlap with CartOrderSummary
						ListHeaderComponent={() => {
							return (
								<View bg="$basicwhite">
									<CheckoutProgress isApprover={isApprover} />
									{/* {!cart?.siteId && !cart?.pickupLocationId && isSignedIn && (
										<Box bg="$neutral25" gap={16} padding={24} borderRadius={6}>
											<Box flexDirection="row" gap={12} borderRadius={6} borderWidth={1} borderColor="$warningbright" bg="$warningdisabled" paddingVertical={6} paddingHorizontal={8}>
												<TriangleAlertIcon size={18} color={resolvedWarningMain} />
												{!cart?.siteId && !cart?.pickupLocationId && (
													<Box flex={1}>
														<ParagraphSmall>
															<Text style={{ fontFamily: "Roboto_700Bold" }}>Please select a site to continue. </Text>
															<Text>We noticed that you haven't chosen a site yet. Selecting the appropriate site helps us provide accurate information.</Text>
														</ParagraphSmall>
													</Box>
												)}
											</Box>
											<HStack alignItems="center" gap={16}>
												<Box flex={isMobile ? 1 : undefined}>
													<SiteSelectButton
														value={{
															id: cart?.siteId || 0,
															name: cart?.siteName || "",
														}}
														title="Site"
														onSelect={updateSite}
													>
														<Button variant="primary-light" borderRadius={12} gap={4} justifyContent="flex-start">
															<ParagraphSmall
																sx={{
																	color: "$basicwhite",
																}}
															>
																Select Site for Delivery
															</ParagraphSmall>
														</Button>
													</SiteSelectButton>
												</Box>
												<Box flex={isMobile ? 1 : undefined}>
													<Button borderRadius={12} gap={4} variant="outline" onPress={() => setIsPickupModalOpen(true)}>
														<ParagraphSmall
															sx={{
																color: "$primarylight",
															}}
														>
															Pickup order
														</ParagraphSmall>
													</Button>
												</Box>

												{!isMobile && (
													<CartPickupLocationModal
														isVisible={isPickupModalOpen}
														onClose={() => setIsPickupModalOpen(false)}
														onLocationSelect={(location) => {
															setSelectedLocation(location);
															updateCart({
																cart: {
																	pickupLocationId: location.id,
																	shippingType: "pickup",
																},
																id: cart?.id as number,
															});
															setIsPickupModalOpen(false);
														}}
														cartId={cart?.id as number}
														selectedLocationId={cart?.pickupLocationId || undefined}
													/>
												)}
											</HStack>
										</Box>
									)} */}
									<Box borderBottomWidth={1} borderBottomColor="$neutral100" alignItems="flex-end" paddingHorizontal={12} paddingVertical={32}>
										<HStack w="$full" justifyContent="space-between">
											<H3>Products</H3>
											<H4>{cartLines.length} Item(s)</H4>
										</HStack>
									</Box>
									<Box paddingHorizontal={12}>{cart && cart.approvalHistory && cart.approvalHistory.length > 0 && <CartApprovalHistory approvalHistory={cart.approvalHistory as CartApproval[]} />}</Box>
								</View>
							);
						}}
						renderItem={({ item, index }: any) => {
							console.log(`Rendering mobile item ${index}: ${item.item?.stockcode}`);
							return <ItemListItem location={cart?.locationId} variant={cart.hasCompleted ? "order" : canEdit ? "checkout" : "order"} removeItem={removeItem} itemWithQuantity={item} showRechargeable={true} />;
						}}
						ListEmptyComponent={
							<>
								{!cart?.siteId && !cart?.pickupLocationId && isSignedIn && (
									<Box paddingVertical={24} backgroundColor="$neutral25" flex={1} paddingHorizontal={12}>
										<Box bg="$basicwhite" borderRadius={6} padding={24}>
											<DismissableContent icon={<TriangleAlertIcon size={18} color={"black"} />} variant="warning">
												<Typography>Please select a site to continue. We noticed that you haven't chosen a site yet. Selecting the appropriate site helps us provide accurate information.</Typography>
											</DismissableContent>
											<HStack gap={12} paddingVertical={12}>
												<Box flex={1}>
													<SiteSelectButton
														value={{
															id: cart?.siteId || 0,
															name: cart?.siteName || "",
														}}
														title="Site"
														onSelect={updateSite}
													>
														<Button variant="primary-light" borderRadius={12} gap={4}>
															<ParagraphSmall
																sx={{
																	color: "$basicwhite",
																}}
															>
																Select Site for Delivery
															</ParagraphSmall>
														</Button>
													</SiteSelectButton>
												</Box>
												<Box flex={isMobile ? 1 : undefined}>
													<Button borderRadius={12} gap={4} variant="outline" onPress={() => setIsPickupModalOpen(true)}>
														<ParagraphSmall
															sx={{
																color: "$primarylight",
															}}
														>
															Pickup order
														</ParagraphSmall>
													</Button>
												</Box>
											</HStack>
										</Box>
										<CartPickupLocationModal
											isVisible={isPickupModalOpen}
											onClose={() => setIsPickupModalOpen(false)}
											onLocationSelect={(location) => {
												setSelectedLocation(location);
												updateCart({
													cart: {
														pickupLocationId: location.id,
														shippingType: "pickup",
													},
													id: cart?.id as number,
												});
												setIsPickupModalOpen(false);
											}}
											cartId={cart?.id as number}
											selectedLocationId={cart?.pickupLocationId || undefined}
										/>
									</Box>
								)}
								<Box gap={16} bg="$neutral25" paddingVertical={28} paddingHorizontal={20}>
									<Box>
										<H2>Cart is empty</H2>
										<Typography sx={{ lineHeight: 24, fontFamily: "Roboto_400Regular" }} weight="normal">
											Your shopping cart is currently empty. Please explore our wide selection of products and don't hesitate to contact our customer support team for any assistance you may need.
										</Typography>
									</Box>
									<HStack gap={6}>
										<Box flex={1}>
											<Link href="/products">
												<Box bg="$primarylight" borderRadius={8} paddingHorizontal={24} paddingVertical={8} alignItems="center">
													<ParagraphSmall sx={{ color: "$basicwhite" }}>Browse Products</ParagraphSmall>
												</Box>
											</Link>
										</Box>
										<Box flex={1}>
											<Link href="/equipment">
												<Box borderWidth={1} borderColor="$primarylight" bg="$basicwhite" borderRadius={8} paddingHorizontal={24} paddingVertical={8} alignItems="center">
													<ParagraphSmall sx={{ color: "$primarylight" }}>Browse equipment</ParagraphSmall>
												</Box>
											</Link>
										</Box>
									</HStack>
								</Box>
							</>
						}
						ListFooterComponent={
							<>
								{cartLines.length > 0 ? (
									<Button bg="$basicwhite" marginBottom={5} borderRadius={8} p={12} gap={6} onPress={clearCartLines}>
										<Icon color={"$primarylight"} as={Trash2} />
										<Typography.Paragraph sx={{ color: "$primarylight" }} weight="normal">
											Remove all items
										</Typography.Paragraph>
									</Button>
								) : null}
								{cartLines.length <= 0 && (
									<>
										<Box paddingVertical={24} bg="$neutral25" borderRadius={12} gap={24} paddingHorizontal={20}>
											<H2>Recommendations</H2>
										</Box>
										<ItemCardList location={cart?.locationId} isGridView={false} isOnNavigation list={recommendedProductCollection} />
									</>
								)}
							</>
						}
					/>
				</Box>
			</CartCheckoutLayout>
		);
	}
	return (
		<CartCheckoutLayout canEdit={canEdit}>
			{cart && cart.approvalHistory && cart.approvalHistory.length > 0 && <CartApprovalHistory approvalHistory={cart.approvalHistory as CartApproval[]} />}
			<ScrollContainer withPadding={false} withFooter={false}>
				{cartLines.length > 0 && (
					<Box paddingHorizontal={20}>
						<HStack alignItems="center" justifyContent="space-between" paddingBottom={28}>
							<H3>Products</H3>
							<Typography.Paragraph weight="normal">{`${cart?.lines ? cart.lines?.length : 0} item${cart?.lines ? (cart.lines?.length > 1 ? "s" : "") : ""}`}</Typography.Paragraph>
						</HStack>
					</Box>
				)}

				{cartLines.length === 0 && (
					<Section gap={16}>
						<H3>Cart is empty</H3>
						<ParagraphSmall sx={{ lineHeight: 21 }} weight="normal">
							Your shopping cart is currently empty. Please explore our wide selection of products and don't hesitate to contact our customer support team for any assistance you may need.
						</ParagraphSmall>
						<HStack gap={6}>
							<Link href="/products">
								<Box bg="$primarylight" borderRadius={8} paddingHorizontal={24} paddingVertical={8}>
									<ParagraphSmall sx={{ color: "$basicwhite" }}>Browse Products</ParagraphSmall>
								</Box>
							</Link>
							<Link href="/equipment">
								<Box borderWidth={1} borderColor="$primarylight" bg="$basicwhite" borderRadius={8} paddingHorizontal={24} paddingVertical={8}>
									<ParagraphSmall sx={{ color: "$primarylight" }}>Browse equipment</ParagraphSmall>
								</Box>
							</Link>
						</HStack>
					</Section>
				)}

				{cartLines.map((item: any, index: number) => {
					const canBeFulfilledStatus = canBeFulfilled(item, cart);
					let backgroundColor = "$basicwhite";

					if (canBeFulfilledStatus >= 50) {
						backgroundColor = "$errordisabled";
					} else if (canBeFulfilledStatus === 40) {
						// backgroundColor = "$warningdisabled";
					}

					let message = "";
					if (canBeFulfilledStatus === 60) {
						message = `This item is discontinued and will not be available for purchase.`;
					}

					if (canBeFulfilledStatus === 50) {
						message = `This item is discontinued and will not be available for purchase. Please choose an alternative.`;
					}

					return (
						<Box key={`${item.item.stockcode}-${index}`} width={"$full"} bg={backgroundColor}>
							<ItemListItem removeItem={removeItem} variant={cart.hasCompleted ? "order" : canEdit ? "checkout" : "order"} itemWithQuantity={item} showRechargeable={true} onRechargeableChange={handleRechargeableChange} />
							{canBeFulfilledStatus === 60 ||
								(canBeFulfilledStatus === 50 && (
									<Box padding={12} bg="$errordisabled" borderRadius={6} marginTop={12}>
										<Typography.ParagraphSmall color="$errorbright">{message}</Typography.ParagraphSmall>
									</Box>
								))}
						</Box>
					);
				})}

				{cartLines.length > 0 && (
					<Button bg="$basicwhite" my={16} borderRadius={8} p={12} gap={6} onPress={clearCartLines}>
						<Icon color={"$primarylight"} as={Trash2} />
						<Typography.Paragraph sx={{ color: "$primarylight" }} weight="normal">
							Remove all items
						</Typography.Paragraph>
					</Button>
				)}
			</ScrollContainer>
			{cartLines.length <= 0 && (
				<Box bg="$neutral25" marginVertical={48} paddingVertical={12} borderRadius={12} gap={24}>
					<Box paddingHorizontal={12}>
						<H3>Recommended Products</H3>
					</Box>
					<ItemCardList isOnNavigation list={recommendedProductCollection} />
				</Box>
			)}
		</CartCheckoutLayout>
	);
}
